import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { DAALUU_FLIGHT_MS, DAALUU_JANLII_ANNOUNCEMENT_MS, DAALUU_JANLII_PICK_MS, DAALUU_KINDS, daaluuBatchRelayTiles, daaluuKind, daaluuRequiredTributeTiles, validDaaluuTributeReturn, legalDaaluuAravguiContributions, legalDaaluuHandPlays, legalDaaluuPairContributions, type DaaluuTile as Tile } from '@big2/shared';
import { useGameState } from '../../../net/GameProvider.js';
import { useI18n } from '../../../i18n.js';
import { ReactionBubble, ReactionPicker } from '../../ReactionUI.js';
import { VoiceChatBar } from '../../VoiceChatBar.js';
import { ConfettiShower } from '../../WinCelebration.js';
import { getUserAvatar } from '../../../utils/avatar.js';
import { Icon } from '../../Icon.js';
import { useDaaluuSounds } from './useDaaluuSounds.js';
import { DaaluuSelfStatus } from './DaaluuSelfStatus.js';
import { DaaluuSizeControl } from './DaaluuSizeControl.js';
import { DaaluuAutoPlayButton } from './DaaluuAutoPlayButton.js';
import { PauseOverlay } from '../common/PauseOverlay.js';
import { TurnIndicator } from '../common/TurnIndicator.js';
import { DaaluuShuffleTable } from './DaaluuShuffleTable.js';
import { DaaluuHand } from './DaaluuHand.js';
import { DaaluuTile } from './DaaluuTile.js';
import { DaaluuTable, DaaluuPlayedPile, DaaluuJanliiChoices } from './DaaluuTable.js';
import { DaaluuDistribution } from './DaaluuDistribution.js';
import { DaaluuJanliiReveal, type JanliiChoicePosition } from './DaaluuJanliiReveal.js';
import { DaaluuMotion, useDaaluuMotion } from './DaaluuMotion.js';
import { playCardSelectSound, playTimerRedTick, playYourTurnSound } from '../../../sound.js';
import { TableInteractionsProvider } from '../TableInteractions.js';

interface TributeReturnDrag { tileId: string; slaveSeat: number; pointerId: number; x: number; y: number; active: boolean; }
interface ReturnFlight { key: number; tile: Tile; from: DOMRect; to: DOMRect; }

export function DaaluuGameScreen({ onOpenProfile }: { onOpenProfile?: () => void }) {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const mn = lang === 'mn', game = state.game, d = game?.daaluu;
  const [selected, setSelected] = useState<string[]>([]);
  const [draggingPlayId, setDraggingPlayId] = useState<string | null>(null);
  const [returnRows, setReturnRows] = useState<Record<number, string[]>>({});
  const pendingReturnFlights = useRef(new Map<string, { slaveSeat: number; tile: Tile; from: DOMRect }>());
  const returnFlightSequence = useRef(0);
  const [returnFlights, setReturnFlights] = useState<ReturnFlight[]>([]);
  const returnDragRef = useRef<TributeReturnDrag | null>(null);
  const [returnDragGhost, setReturnDragGhost] = useState<{ tileId: string; x: number; y: number } | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const prevTurnPlayerRef = useRef<string | null>(null);
  const lastRedTickSecRef = useRef<number | null>(null);
  const seenCelebrationRef = useRef(`${d?.shuffle.roundId}:${d?.motion?.serial}`);
  const seenJanliiRef = useRef(`${d?.shuffle.roundId}:${d?.janlii ?? ''}`);
  const celebrationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const janliiPickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const janliiChoicesRef = useRef<HTMLDivElement>(null);
  const janliiChoicePositionsRef = useRef<JanliiChoicePosition[]>([]);
  const [celebration, setCelebration] = useState<{ key: string; label: string; player: string; janliiKindId?: string } | null>(null);
  const [janliiPick, setJanliiPick] = useState<{ key: string; kindId: string; player: string; choices: JanliiChoicePosition[] } | null>(null);
  const handKey = d?.hand.map(tile => tile.id).join(',');
  useEffect(() => setSelected([]), [d?.round, handKey, d?.stage]);
  useEffect(() => setReturnRows({}), [d?.round, d?.stage, d?.tributeBatch?.phase]);
  useLayoutEffect(() => {
    if (!pendingReturnFlights.current.size) return;
    const pending = [...pendingReturnFlights.current];
    pendingReturnFlights.current.clear();
    const flights: ReturnFlight[] = [];
    for (const [tileId, { slaveSeat, tile: assignedTile, from }] of pending) {
      const row = document.querySelector<HTMLElement>(`[data-daaluu-tribute-target="${slaveSeat}"]`);
      const tile = Array.from(row?.querySelectorAll<HTMLElement>('[data-daaluu-board-tile]') ?? [])
        .find(element => element.dataset.daaluuBoardTile === tileId);
      if (!tile) continue;
      flights.push({ key: ++returnFlightSequence.current, tile: assignedTile, from, to: tile.getBoundingClientRect() });
    }
    if (flights.length) setReturnFlights(current => [...current, ...flights]);
  }, [returnRows]);
  useEffect(() => { setDraggingPlayId(null); returnDragRef.current = null; setReturnDragGhost(null); }, [d?.round, d?.stage]);
  useLayoutEffect(() => {
    if (d?.stage !== 'calling') return;
    const rememberChoices = () => {
      const buttons = janliiChoicesRef.current?.querySelectorAll<HTMLButtonElement>('.daaluu-face-tile');
      if (!buttons?.length) return;
      janliiChoicePositionsRef.current = DAALUU_KINDS.filter(kind => kind.copies === 2).map((kind, index) => {
        const { left, top, width } = buttons[index].getBoundingClientRect();
        return { kindId: kind.id, left, top, width };
      });
    };
    rememberChoices();
    window.addEventListener('resize', rememberChoices);
    window.addEventListener('scroll', rememberChoices, true);
    return () => {
      window.removeEventListener('resize', rememberChoices);
      window.removeEventListener('scroll', rememberChoices, true);
    };
  }, [d?.stage, d?.shuffle.roundId]);
  const isPaused = game?.pause?.status === 'paused';
  useEffect(() => {
    if (!game || isPaused || !game.activePlayerId) return;
    if (game.activePlayerId !== prevTurnPlayerRef.current) {
      prevTurnPlayerRef.current = game.activePlayerId;
      lastRedTickSecRef.current = null;
      if (game.activePlayerId === game.myPlayerId) playYourTurnSound();
    }
  }, [game?.activePlayerId, game?.myPlayerId, isPaused]);
  useEffect(() => {
    if (!game?.timeoutEndsAt || isPaused) {
      setTimeLeft(null);
      lastRedTickSecRef.current = null;
      return;
    }
    const tick = () => {
      const remain = Math.max(0, game.timeoutEndsAt! - (Date.now() + (state.serverOffsetMs ?? 0)));
      setTimeLeft(remain);
      if (game.activePlayerId === game.myPlayerId && remain > 0 && remain <= 5000) {
        const sec = Math.ceil(remain / 1000);
        if (lastRedTickSecRef.current !== sec) {
          lastRedTickSecRef.current = sec;
          playTimerRedTick(sec);
        }
      }
    };
    tick();
    const timer = setInterval(tick, 100);
    return () => clearInterval(timer);
  }, [game?.timeoutEndsAt, game?.activePlayerId, game?.myPlayerId, isPaused, state.serverOffsetMs]);
  useEffect(() => {
    if (!game || !d?.motion) return;
    const key = `${d.shuffle.roundId}:${d.motion.serial}`;
    if (key === seenCelebrationRef.current) return;
    const sameRound = seenCelebrationRef.current.startsWith(`${d.shuffle.roundId}:`);
    seenCelebrationRef.current = key;
    if (!sameRound) {
      if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
      celebrationTimerRef.current = null;
      setCelebration(null);
      return;
    }
    const publicPlays = [...d.trick, ...(d.lastTrick?.plays ?? [])];
    const move = d.motion.movements.find(movement => movement.from === 'hand' && movement.to === 'board' &&
      publicPlays.some(play => play.seat === movement.fromSeat && play.tiles.length === movement.tiles.length &&
        play.tiles.every(tile => movement.tiles.some(moved => moved.id === tile.id))));
    if (!move?.tiles.length) return;
    const kindId = move.tiles[0]?.kindId;
    const pair = move.tiles.length === 2 && move.tiles.every(tile => tile.kindId === kindId);
    const samePlay = (play: { seat: number; tiles: typeof move.tiles }) => play.seat === move.fromSeat &&
      play.tiles.length === move.tiles.length && play.tiles.every(tile => move.tiles.some(moved => moved.id === tile.id));
    const activeIndex = d.trick.findIndex(samePlay);
    const completedIndex = d.lastTrick?.plays.findIndex(samePlay) ?? -1;
    const beat = activeIndex > 0 && d.winningSeat === move.fromSeat ||
      completedIndex > 0 && d.lastTrick?.winnerSeat === move.fromSeat;
    const leadingSingle = move.tiles.length === 1 && (activeIndex === 0 || completedIndex === 0);
    const winningJanlii = kindId === d.janlii && (move.tiles.length === 1 || pair) &&
      (activeIndex === 0 || completedIndex === 0 || beat);
    const kindName = daaluuKind(move.tiles[0]).name;
    const label = winningJanlii ? `${mn ? 'Жанлий' : 'Janlii'} · ${kindName}`
      : (beat || leadingSingle) && kindId === 'daaluu' ? (pair ? (mn ? 'Хос Даалуу' : 'Daaluu pair') : kindName)
      : (beat || leadingSingle) && kindId === 'uuluu' ? (pair ? (mn ? 'Хос Үүлүү' : 'Uuluu pair') : kindName) : null;
    if (!label) return;
    if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
    setCelebration({ key, label, player: game.players[move.fromSeat]?.name ?? '' });
    celebrationTimerRef.current = setTimeout(() => { setCelebration(null); celebrationTimerRef.current = null; }, 2500);
  }, [game?.daaluu?.motion?.serial, game?.daaluu?.shuffle.roundId, game?.daaluu?.trick, game?.daaluu?.lastTrick, game?.players, mn]);
  useEffect(() => {
    if (!game || !d) return;
    const roundId = d.shuffle.roundId;
    const key = `${roundId}:${d.janlii ?? ''}`;
    if (key === seenJanliiRef.current) return;
    const calledThisRound = seenJanliiRef.current === `${roundId}:` && !!d.janlii;
    seenJanliiRef.current = key;
    if (!calledThisRound) {
      if (janliiPickTimerRef.current) clearTimeout(janliiPickTimerRef.current);
      janliiPickTimerRef.current = null;
      setJanliiPick(null);
      return;
    }
    const kind = DAALUU_KINDS.find(candidate => candidate.id === d.janlii);
    if (!kind) return;
    if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
    setCelebration(null);
    if (janliiPickTimerRef.current) clearTimeout(janliiPickTimerRef.current);
    const player = game.players[d.callerSeat]?.name ?? '';
    const choices = janliiChoicePositionsRef.current;
    setJanliiPick(choices.some(choice => choice.kindId === kind.id) ? { key, kindId: kind.id, player, choices } : null);
    const pickDelay = !choices.length || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : DAALUU_JANLII_PICK_MS;
    janliiPickTimerRef.current = setTimeout(() => {
      setJanliiPick(null);
      setCelebration({
        key: `janlii:${key}`,
        label: `${mn ? 'Жанлий' : 'Janlii'} · ${kind.name}`,
        player: `${player} ${mn ? 'дуудлаа' : 'called it'}`.trim(),
        janliiKindId: kind.id,
      });
      celebrationTimerRef.current = setTimeout(() => { setCelebration(null); celebrationTimerRef.current = null; }, DAALUU_JANLII_ANNOUNCEMENT_MS + DAALUU_JANLII_PICK_MS - pickDelay);
      janliiPickTimerRef.current = null;
    }, pickDelay);
  }, [game?.daaluu?.janlii, game?.daaluu?.shuffle.roundId, game?.daaluu?.callerSeat, game?.players, mn]);
  useEffect(() => () => {
    if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
    if (janliiPickTimerRef.current) clearTimeout(janliiPickTimerRef.current);
  }, []);
  const motion = useDaaluuMotion(game);
  useDaaluuSounds(game);
  if (!game || !d) return null;
  const myTurn = game.activePlayerId === game.myPlayerId;
  const disabled = !myTurn || !!d.leaderSkips || state.connectionStatus !== 'connected' || game.pause?.status === 'paused' || motion.flights.length > 0;
  const canOfferBailout = d.stage === 'bailout' && d.bailoutSeats.includes(game.mySeat) && !d.processedBailoutSeats.includes(game.mySeat)
    && !d.bailoutLoans.some(loan => loan.buyerSeat === game.mySeat) && state.connectionStatus === 'connected' && game.pause?.status !== 'paused';
  const selectionDisabled = state.connectionStatus !== 'connected' || game.pause?.status === 'paused' ||
    !['playing', 'pair-response', 'aravgui-call', 'aravgui-response', 'tribute-give', 'tribute-return'].includes(d.stage);
  const prep = d.stage === 'mixing' || d.stage === 'retry';
  const collecting = d.stage === 'selecting' || d.stage === 'distributing';
  const janliiRevealActive = collecting && (
    d.stage === 'selecting' && seenJanliiRef.current === `${d.shuffle.roundId}:` ||
    !!janliiPick || !!celebration?.janliiKindId
  );
  const contributing = d.stage === 'pair-response';
  const exchanging = d.stage === 'tribute-give' || d.stage === 'tribute-return';
  const revealingTribute = d.stage === 'tribute-reveal' || d.stage === 'tribute-relay-reveal';
  const aravgui = d.stage === 'aravgui-call' || d.stage === 'aravgui-response';
  const giving = d.stage === 'tribute-give';
  const tributeBatch = d.tributeBatch;
  const batchGiveRelay = tributeBatch?.phase === 'relay-give';
  const relayReturnOffer = tributeBatch?.phase === 'relay-return' ? tributeBatch.offers[tributeBatch.relayIndex] : null;
  const showingMasterRound = tributeBatch?.phase === 'giving' || tributeBatch?.phase === 'master-return';
  const showingSiblingRound = tributeBatch?.phase === 'relay-return';
  const hideSiblingStepLabel = batchGiveRelay;
  const requiredGifts = batchGiveRelay ? daaluuBatchRelayTiles(d.hand, d.janlii)
    : daaluuRequiredTributeTiles(d.hand, d.janlii, !!d.tribute?.relay);
  const returningRelay = !!d.tribute?.relay?.offered.length && !d.tribute.relay.returned.length;
  const legacyRelayTransferred = !!d.tribute?.relay?.returned.length && d.stage === 'tribute-return';
  const batchReturnCount = tributeBatch?.phase === 'master-return'
    ? tributeBatch.offers.reduce((count, offer) => count + offer.big.length, 0)
    : tributeBatch?.phase === 'relay-return' ? tributeBatch.offers[tributeBatch.relayIndex]?.middle.length ?? 0 : 0;
  const masterBatchReveal = tributeBatch?.phase === 'master-return' && d.stage === 'tribute-reveal';
  const exchangeCount = giving ? requiredGifts.length : tributeBatch ? batchReturnCount
    : returningRelay ? d.tribute!.relay!.offered.length : d.tribute?.offered.length ?? 0;
  const returnRankLimit = tributeBatch ? tributeBatch.phase === 'relay-return' ? 8 : 10
    : returningRelay ? 10 : d.tribute?.relay ? 8 : 10;
  const masterBatchReturn = tributeBatch?.phase === 'master-return' && d.stage === 'tribute-return';
  const assignedReturnIds = masterBatchReturn ? tributeBatch.offers.flatMap(offer => returnRows[offer.slaveSeat] ?? []) : [];
  const assignedSet = new Set(assignedReturnIds);
  const assignedRowsComplete = !!masterBatchReturn && tributeBatch.offers.every(offer => (returnRows[offer.slaveSeat]?.length ?? 0) === offer.big.length);
  const exchangeLegal = giving ? selected.length === requiredGifts.length && requiredGifts.every(tile => selected.includes(tile.id))
    : masterBatchReturn ? assignedRowsComplete && assignedReturnIds.length === exchangeCount &&
      validDaaluuTributeReturn(d.hand, d.janlii, exchangeCount, assignedReturnIds, returnRankLimit)
      : validDaaluuTributeReturn(d.hand, d.janlii, exchangeCount, selected, returnRankLimit);
  const collectionColor = d.pairCollection?.color === 'red' ? (mn ? 'улаан' : 'red') : (mn ? 'цагаан' : 'white');
  const own = game.players.find(player => player.playerId === game.myPlayerId)!;
  const myAvatar = getUserAvatar(own.playerId, own.name);
  const legalPlays = d.stage === 'playing' ? legalDaaluuHandPlays({ hand: d.hand, janlii: d.janlii, trick: d.trick,
    winningSeat: d.winningSeat, deadTileIds: d.deadTileIds, lowTrick: d.lowTrick, capturePiles: d.capturePiles, followColor: d.followColor }) : contributing && d.pairCollection ? legalDaaluuPairContributions(d.hand, d.pairCollection.color) : [];
  const selectionLimit = exchanging ? exchangeCount : Math.max(2, ...legalPlays.map(play => play.length));
  const selectionLegal = exchanging ? exchangeLegal && (masterBatchReturn || selected.length > 0)
    : aravgui ? d.stage === 'aravgui-call' ? selected.length === 2 && selected.every(id => d.hand.some(tile => tile.id === id))
      : legalDaaluuAravguiContributions(d.hand, d.janlii).some(pair => pair.every(tile => selected.includes(tile.id)))
    : legalPlays.some(play => play.length === selected.length && play.every(tile => selected.includes(tile.id)));
  const draggedTileIds = draggingPlayId === null ? [] : selected.includes(draggingPlayId) ? selected : [draggingPlayId];
  const dragPlayLegal = d.stage === 'playing' && legalPlays.some(play => play.length === draggedTileIds.length &&
    play.every(tile => draggedTileIds.includes(tile.id)));
  const canDropPlay = draggingPlayId === null ? selectionLegal : dragPlayLegal;
  function assignReturnToSlave(draggedId: string, slaveSeat: number) {
    if (!d || !masterBatchReturn || disabled) return;
    const moving = (selected.includes(draggedId) ? selected : [draggedId]).filter(id => !assignedSet.has(id));
    const offer = tributeBatch?.offers.find(item => item.slaveSeat === slaveSeat);
    if (!offer || !moving.length || moving.length > offer.big.length - (returnRows[slaveSeat]?.length ?? 0)) return;
    for (const id of moving) {
      const source = Array.from(document.querySelectorAll<HTMLElement>('[data-daaluu-hand-tile]'))
        .find(element => element.dataset.daaluuHandTile === id);
      const from = source?.getBoundingClientRect();
      const tile = d.hand.find(item => item.id === id);
      if (from && tile) pendingReturnFlights.current.set(id, { slaveSeat, tile, from });
    }
    setReturnRows(current => ({ ...current, [slaveSeat]: [...(current[slaveSeat] ?? []), ...moving] }));
    setSelected([]);
  }
  function moveReturnToSlave(tileId: string, fromSeat: number, toSeat: number) {
    if (!masterBatchReturn || fromSeat === toSeat || disabled) return;
    if (!tributeBatch?.offers.some(offer => offer.slaveSeat === toSeat)) return;
    setReturnRows(current => {
      const from = current[fromSeat] ?? [], to = current[toSeat] ?? [];
      if (!from.includes(tileId)) return current;
      return { ...current, [fromSeat]: from.filter(id => id !== tileId), [toSeat]: [...to, tileId] };
    });
  }
  function returnTileToHand(tileId: string, fromSeat: number) {
    setReturnRows(current => {
      const row = current[fromSeat] ?? [];
      return row.includes(tileId) ? { ...current, [fromSeat]: row.filter(id => id !== tileId) } : current;
    });
  }
  function startReturnDrag(event: ReactPointerEvent<HTMLSpanElement>, tileId: string, slaveSeat: number) {
    if (event.button !== 0 || !masterBatchReturn || disabled) return;
    returnDragRef.current = { tileId, slaveSeat, pointerId: event.pointerId, x: event.clientX, y: event.clientY, active: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }
  function moveReturnDrag(event: ReactPointerEvent<HTMLSpanElement>) {
    const drag = returnDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.active && Math.hypot(event.clientX - drag.x, event.clientY - drag.y) < 8) return;
    drag.active = true;
    setDraggingPlayId(drag.tileId);
    setReturnDragGhost({ tileId: drag.tileId, x: event.clientX, y: event.clientY });
    event.preventDefault();
  }
  function finishReturnDrag(event: ReactPointerEvent<HTMLSpanElement>) {
    const drag = returnDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const target = drag.active
      ? document.elementFromPoint?.(event.clientX, event.clientY)?.closest<HTMLElement>('[data-daaluu-tribute-target]')
      : null;
    const handTarget = drag.active
      ? document.elementFromPoint?.(event.clientX, event.clientY)?.closest<HTMLElement>('[data-daaluu-tribute-hand-target]')
      : null;
    const targetSeat = Number(target?.dataset.daaluuTributeTarget);
    if (target && Number.isInteger(targetSeat)) moveReturnToSlave(drag.tileId, drag.slaveSeat, targetSeat);
    else if (handTarget) returnTileToHand(drag.tileId, drag.slaveSeat);
    returnDragRef.current = null;
    setDraggingPlayId(null);
    setReturnDragGhost(null);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }
  function cancelReturnDrag(event: ReactPointerEvent<HTMLSpanElement>) {
    if (returnDragRef.current?.pointerId !== event.pointerId) return;
    returnDragRef.current = null;
    setDraggingPlayId(null);
    setReturnDragGhost(null);
  }
  function submitExchange() {
    if (masterBatchReturn && tributeBatch) {
      actions.daaluuAction({ type: 'tribute', tileIds: assignedReturnIds,
        returnsBySeat: tributeBatch.offers.filter(offer => offer.big.length > 0)
          .map(offer => ({ slaveSeat: offer.slaveSeat, tileIds: returnRows[offer.slaveSeat] ?? [] })) });
      return;
    }
    actions.daaluuAction({ type: aravgui ? 'aravgui' : exchanging ? 'tribute' : 'play', tileIds: selected });
  }
  const submitCount = masterBatchReturn ? assignedReturnIds.length : selected.length;
  const reaction = state.reactions[game.myPlayerId];
  return (
    <TableInteractionsProvider onOpenProfile={onOpenProfile}>
      <div className={`game-screen daaluu-game-screen${collecting && !janliiRevealActive ? ' is-distributing' : ''}${revealingTribute && !tributeBatch ? ' is-tribute-reveal' : ''}`}>
        <div className="game-table-area daaluu-game-area">
          <PauseOverlay />
      <DaaluuTable game={game} mn={mn} movingTileIds={motion.capturing} showJanliiMarker={!janliiRevealActive}
        onOfferHouse={d.stage === 'debt-choice' && state.connectionStatus === 'connected' && game.pause?.status !== 'paused'
          ? (debtorSeat, pileId) => actions.daaluuAction({ type: 'debt-choice', debtorSeat, pileId }) : undefined} reactionFor={playerId => {
        const remote = state.reactions[playerId];
        return remote && playerId !== game.myPlayerId ? <ReactionBubble reactionId={remote.reactionId} seq={remote.seq} playerName={game.players.find(player => player.playerId === playerId)!.name} /> : null;
      }}>
      {prep ? <DaaluuShuffleTable embedded view={d.shuffle} mySeat={game.mySeat} players={game.players}
        deadline={game.timeoutEndsAt} connected={state.connectionStatus === 'connected' && game.pause?.status !== 'paused'} lang={lang}
        onSweep={actions.daaluuSweep} onRetry={actions.daaluuRetry} canRetry={state.lobby?.hostId === game.myPlayerId}
        />
        : <section className={`daaluu-play-table${draggingPlayId !== null && dragPlayLegal ? ' is-play-drop-target' : ''}`} data-daaluu-play-drop={d.stage === 'playing' && canDropPlay && !disabled ? '' : undefined} data-drop-label={mn ? 'Модоо энд тавина уу' : 'Drop tiles here'} aria-label={mn ? 'Даалууны ширээ' : 'Daaluu table'}>
          {d.stage === 'calling' && <div><p>{mn ? 'Дуудагч жанлий сонгоно.' : 'The caller chooses жанлий before seeing any hand.'}</p>
            <DaaluuJanliiChoices choicesRef={janliiChoicesRef} disabled={disabled} mn={mn} onChoose={kindId => actions.daaluuAction({ type: 'call', kindId })} />
          </div>}
          {!janliiRevealActive && <DaaluuDistribution game={game} mn={mn} disabled={disabled} onCollect={actions.daaluuAction} />}
          {d.stage === 'bailout' && <div><p>{canOfferBailout
            ? mn ? 'Өрийг төлөх санал өгөх эсвэл өнжих.' : 'Offer to pay a debtor’s creditors, or decline. The debtor chooses if several players offer.'
            : d.bailoutLoans.some(loan => loan.buyerSeat === game.mySeat)
              ? mn ? 'Зээлдүүлэгчийн хариуг хүлээж байна.' : 'Waiting for the lender to answer your request.'
            : mn ? 'Бусад чөлөөт тоглогчдын саналыг хүлээж байна.' : 'Waiting for free players to respond.'}</p>
            {canOfferBailout && d.eligibleDebtors.filter(seat => d.bailoutBuyers[seat]?.includes(game.mySeat)).map(debtor => {
              const cost = d.ledger.debt[debtor].reduce((sum, debt, creditor) => sum + (creditor === game.mySeat ? 0 : debt), 0);
              const shortage = Math.max(0, cost - d.ledger.tea[game.mySeat]);
              return <div key={debtor}>{shortage === 0
                ? <button disabled={!canOfferBailout} onClick={() => actions.daaluuAction({ type: 'bailout', debtorSeat: debtor })}>{mn ? 'Санал өгөх' : 'Offer for'} {game.players[debtor].name}</button>
                : game.players.filter(player => player.seat !== game.mySeat && player.seat !== debtor && d.ledger.masters[player.seat] === null && d.ledger.tea[player.seat] >= shortage)
                  .map(lender => <button key={lender.seat} disabled={!canOfferBailout} onClick={() => actions.daaluuAction({ type: 'bailout', debtorSeat: debtor, lenderSeat: lender.seat })}>
                    {mn ? 'Зээл хүсэх' : 'Ask'} {lender.name} {mn ? 'зээл' : 'for a loan'} ({shortage}) · {game.players[debtor].name}
                  </button>)}</div>;
            })}
            {canOfferBailout && <button onClick={() => actions.daaluuAction({ type: 'bailout', debtorSeat: null })}>{mn ? 'Өнжих' : 'Decline'}</button>}
          </div>}
          {d.stage === 'bailout' && d.bailoutLoans.filter(loan => loan.lenderSeat === game.mySeat).map(loan =>
            <div key={loan.buyerSeat} role="group" aria-label={`${game.players[loan.buyerSeat].name} · ${mn ? 'Зээлийн хүсэлт' : 'Loan request'}`}>
              <p>{game.players[loan.buyerSeat].name} · {loan.amount} {mn ? 'цай зээлж' : 'tea loan to buy'} {game.players[loan.debtorSeat].name}</p>
              <button disabled={state.connectionStatus !== 'connected' || game.pause?.status === 'paused'} onClick={() => actions.daaluuAction({ type: 'bailout-loan', buyerSeat: loan.buyerSeat, accept: true })}>{mn ? 'Зөвшөөрөх' : 'Accept loan'}</button>
              <button disabled={state.connectionStatus !== 'connected' || game.pause?.status === 'paused'} onClick={() => actions.daaluuAction({ type: 'bailout-loan', buyerSeat: loan.buyerSeat, accept: false })}>{mn ? 'Татгалзах' : 'Decline loan'}</button>
            </div>)}
          {d.stage === 'bailout-choice' && <div role="group" aria-label={mn ? 'Ноён сонгох' : 'Choose a master'}>
            <p>{myTurn ? mn ? 'Таны өрийг төлөх саналууд. Ноёноо сонгоно уу.' : 'These players offered to pay your debt. Choose your master.'
              : mn ? 'Өртэй тоглогч ноёноо сонгож байна.' : 'The debtor is choosing a master.'}</p>
            {myTurn && d.bailoutOffers.filter(offer => offer.debtorSeat === game.mySeat).map(offer =>
              <button key={offer.buyerSeat} disabled={disabled} onClick={() => actions.daaluuAction({ type: 'bailout-choose', buyerSeat: offer.buyerSeat })}>
                {game.players[offer.buyerSeat].name}
              </button>)}
          </div>}
          {d.stage === 'pair-call' && <div><p>{mn ? 'Тараасан модонд хос жанлий байна. Хураах хосын өнгийг сонгоно уу.' : 'A Janlii pair was dealt. Choose the color to collect everyone’s strongest pair and capture two houses.'}</p>
            <div className="daaluu-call-options"><button disabled={disabled} onClick={() => actions.daaluuAction({ type: 'pair-call', color: 'red' })}>{mn ? 'Улаан' : 'Red'}</button><button disabled={disabled} onClick={() => actions.daaluuAction({ type: 'pair-call', color: 'white' })}>{mn ? 'Цагаан' : 'White'}</button></div>
          </div>}
          {contributing && <p role="status">{mn ? `Дуудсан өнгө: ${collectionColor}` : `Called color: ${collectionColor}`}</p>}
          {tributeBatch && !batchGiveRelay && (exchanging || revealingTribute) && <section className="daaluu-tribute-board" aria-label={mn ? 'Ноёны солилцоо' : 'Tribute exchange'}>
            {!hideSiblingStepLabel && <strong>{showingSiblingRound ? (mn ? 'Боолуудын солилцоо' : 'Slave exchange')
              : masterBatchReveal ? (mn ? `Ноён ${exchangeCount} мод буцаалаа` : `Master returned ${exchangeCount} tiles`)
              : revealingTribute ? (mn ? 'Солилцох моднууд' : 'Exchange tiles on the table')
              : batchGiveRelay ? (mn ? 'Хоёр дахь боол найм, есөө эхний боолд өгнө' : 'Second slave gives eights and nines to the first slave')
              : giving ? (mn ? 'Хоёр боолын том модыг ноёнд өгөх' : 'Both slaves give their high tiles to the master')
              : tributeBatch.phase === 'relay-return' ? (mn ? 'Эхний боол жижиг мод буцаана' : 'First slave returns small tiles')
                : (mn ? `${exchangeCount} мод буцаахыг хүлээж байна` : `Master returns ${exchangeCount} tiles`)}</strong>}
            {masterBatchReveal && <p className="daaluu-tribute-help">{mn
              ? 'Ноёны буцаасан моднуудыг боол бүрийн мөрөнд харуулж байна.'
              : 'The master’s returned tiles are shown in each slave’s row.'}</p>}
            <div className={`daaluu-tribute-sets${tributeBatch ? ' is-batch' : ''}${tributeBatch.phase === 'relay-return' ? ' is-sibling-return' : ''}`}>
              {tributeBatch.phase !== 'relay-return' && tributeBatch.offers.map(offer => <div className={`daaluu-tribute-side${showingMasterRound ? ' is-master-round' : ''}`} key={offer.slaveSeat}>
                {showingMasterRound && <><p>{game.players[offer.slaveSeat]?.name} → {game.players[tributeBatch.masterSeat]?.name}</p>
                  <div className="daaluu-tribute-offer">{offer.big.map(tile => <span key={tile.id} data-daaluu-board-tile={tile.id}><DaaluuTile tile={tile} /></span>)}</div></>}
                {showingSiblingRound && offer.middle.length > 0 && <><p>{game.players[offer.slaveSeat]?.name} → {game.players[tributeBatch.firstSlaveSeat]?.name}</p>
                  <div className="daaluu-tribute-offer">{offer.middle.map(tile => <span key={tile.id} data-daaluu-board-tile={tile.id}><DaaluuTile tile={tile} /></span>)}</div></>}
                {showingMasterRound && <>
                  <p>{game.players[tributeBatch.masterSeat]?.name} → {game.players[offer.slaveSeat]?.name}</p>
                  <div className={`daaluu-tribute-return-target${draggingPlayId !== null && game.mySeat === tributeBatch.masterSeat ? ' is-ready' : ''}`}
                    data-daaluu-tribute-target={masterBatchReturn && game.mySeat === tributeBatch.masterSeat ? offer.slaveSeat : undefined}>
                    {(masterBatchReturn ? (returnRows[offer.slaveSeat] ?? []).flatMap(id => { const tile = d.hand.find(item => item.id === id); return tile ? [tile] : []; }) : offer.returned).map(tile =>
                      <span key={tile.id} data-daaluu-board-tile={tile.id}
                        className={motion.boardArriving.includes(tile.id) || returnFlights.some(flight => flight.tile.id === tile.id) ? 'daaluu-tile-arriving' : undefined}
                        onPointerDown={event => startReturnDrag(event, tile.id, offer.slaveSeat)} onPointerMove={moveReturnDrag}
                        onPointerUp={finishReturnDrag} onPointerCancel={cancelReturnDrag} onLostPointerCapture={cancelReturnDrag}>
                        <DaaluuTile tile={tile} /></span>)}
                    {masterBatchReturn && game.mySeat === tributeBatch.masterSeat && draggingPlayId !== null &&
                      !(returnRows[offer.slaveSeat]?.length) && <span className="daaluu-tribute-placeholder">Drop tiles here</span>}
                  </div>
                  {masterBatchReturn && game.mySeat === tributeBatch.masterSeat && selected.length > 0 &&
                    <button type="button" className="daaluu-tribute-row-assign" disabled={selected.length > offer.big.length - (returnRows[offer.slaveSeat]?.length ?? 0)}
                      onClick={() => assignReturnToSlave(selected[0], offer.slaveSeat)}>{mn ? 'Сонгосон мод өгөх' : 'Assign selected tiles'}</button>}
                </>}
              </div>)}
              {tributeBatch.phase === 'relay-return' && relayReturnOffer && <div className="daaluu-tribute-side daaluu-tribute-sibling-offer">
                <p>{game.players[relayReturnOffer.slaveSeat]?.name} → {game.players[tributeBatch.firstSlaveSeat]?.name}</p>
                <div className="daaluu-tribute-offer">{relayReturnOffer.middle.map(tile => <span key={tile.id} data-daaluu-board-tile={tile.id}><DaaluuTile tile={tile} /></span>)}</div>
              </div>}
              {tributeBatch.phase === 'relay-return' && (exchanging || revealingTribute) && !!d.tribute && <div className="daaluu-tribute-side daaluu-tribute-sibling-return">
                <p>{game.players[tributeBatch.firstSlaveSeat]?.name} → {game.players[d.tribute!.slaveSeat]?.name}</p>
                <div className="daaluu-tribute-offer">{revealingTribute
                  ? d.tribute.returned.map(tile => <span key={tile.id} data-daaluu-board-tile={tile.id}
                    className={motion.boardArriving.includes(tile.id) ? 'daaluu-tile-arriving' : undefined}><DaaluuTile tile={tile} /></span>)
                  : <span className="daaluu-tribute-placeholder">{mn ? 'Жижиг модоо энд буцаана' : 'Returned small tiles appear here'}</span>}</div>
              </div>}
            </div>
          </section>}
          {(exchanging || revealingTribute) && d.tribute && !d.tributeBatch && <section className="daaluu-tribute-board" aria-label={mn ? 'Ноёны солилцоо' : 'Tribute exchange'}>
            <strong>{revealingTribute ? (mn ? 'Солилцох моднууд' : 'Exchange tiles on the table')
              : giving ? (mn ? 'Өгөх модоо сонгож байна' : 'Choosing tiles to give')
              : (mn ? `${exchangeCount} модны хариуг хүлээж байна` : `Waiting for ${exchangeCount} tiles in return`)}</strong>
            <div className="daaluu-tribute-sets">
              <div className="daaluu-tribute-side"><p>{game.players[d.tribute.slaveSeat]?.name} → {game.players[d.tribute.relay && (d.stage === 'tribute-relay-reveal' || returningRelay) ? d.tribute.relay.masterSeat : d.tribute.masterSeat]?.name}</p>
                <div className="daaluu-tribute-offer">{(legacyRelayTransferred ? [] : d.tribute.relay && (d.stage === 'tribute-relay-reveal' || returningRelay) ? d.tribute.relay.offered : d.tribute.offered).map(tile => <span key={tile.id} data-daaluu-board-tile={tile.id}><DaaluuTile tile={tile} /></span>)}</div>
              </div>
              {d.tribute.relay && (d.stage === 'tribute-relay-reveal' || returningRelay) && <div className="daaluu-tribute-side"><p>{game.players[d.tribute.slaveSeat]?.name} → {game.players[d.tribute.masterSeat]?.name}</p>
                <div className="daaluu-tribute-offer">{d.tribute.offered.filter(tile => !d.tribute!.relay!.offered.some(high => high.id === tile.id)).map(tile => <span key={tile.id} data-daaluu-board-tile={tile.id}><DaaluuTile tile={tile} /></span>)}</div>
              </div>}
              {revealingTribute && <div className="daaluu-tribute-side"><p>{game.players[d.stage === 'tribute-relay-reveal' ? d.tribute.relay!.masterSeat : d.tribute.masterSeat]?.name} → {game.players[d.stage === 'tribute-relay-reveal' ? d.tribute.masterSeat : d.tribute.slaveSeat]?.name}</p>
                <div className="daaluu-tribute-offer">{(d.stage === 'tribute-relay-reveal' ? d.tribute.relay!.returned : d.tribute.returned).map(tile => <span key={tile.id} data-daaluu-board-tile={tile.id} className={motion.boardArriving.includes(tile.id) ? 'daaluu-tile-arriving' : undefined}><DaaluuTile tile={tile} /></span>)}</div>
              </div>}
            </div>
          </section>}
          {aravgui && d.aravgui && <section className="daaluu-aravgui-board" aria-label="Аравгүй арав">
            <h3>Аравгүй арав</h3>
            <p>{game.players[d.aravgui.holderSeat]?.name} · {mn ? '2 гэр' : '2 houses'}</p>
            <div className="daaluu-aravgui-offer">{d.aravgui.plays.flatMap(play => play.tiles.map(tile =>
              <span key={tile.id} data-daaluu-board-tile={tile.id} className={motion.boardArriving.includes(tile.id) ? 'daaluu-tile-arriving' : undefined}>
                <DaaluuTile tile={tile} dead={play.deadTileIds?.includes(tile.id)} />
              </span>))}</div>
            <small>{d.aravgui.plays.length}/5</small>
          </section>}
          <div data-daaluu-motion-board className="daaluu-motion-board-anchor" aria-hidden="true" />
          <DaaluuPlayedPile plays={motion.settlingTrick?.plays ?? d.trick} winningSeat={motion.settlingTrick?.winnerSeat ?? d.winningSeat} players={game.players} mn={mn} arrivingTileIds={motion.boardArriving} />
        </section>}
      </DaaluuTable>
      <TurnIndicator isMyTurn={!prep && myTurn} timeLeft={timeLeft}
        customPrompt={d.leaderSkips?.seats.length ? mn
          ? `⏭️ ${game.players[d.leaderSkips.seats[0]]?.name}: Эхлэх мод алга. Дараагийн ээлж: ${game.players[(d.leaderSkips.seats[0] + 1) % game.players.length]?.name}.`
          : `⏭️ ${game.players[d.leaderSkips.seats[0]]?.name}: No starting tile. Moving to ${game.players[(d.leaderSkips.seats[0] + 1) % game.players.length]?.name}.`
          : d.stage === 'round-over' ? mn ? 'Тойрог дууслаа' : 'Round complete. Tea and debt have been settled; the next round starts shortly.'
          : d.stage === 'debt-choice' ? (d.debtChoice?.surplus[game.mySeat] ?? 0) > 0
            ? mn ? 'Илүү гэрээ дутуу гэртэй тоглогч руу чирнэ үү' : 'Drag your extra house to a player who needs one'
            : mn ? 'Илүү гэртэй тоглогчдын шилжүүлгийг хүлээнэ үү' : 'Waiting for surplus players to place houses'
          : d.stage === 'bailout' ? mn ? 'Чөлөөт тоглогчдын санал' : 'Free players may make offers'
          : d.stage === 'bailout-choice' ? mn ? 'Өртэй тоглогч ноёноо сонгоно' : 'Debtor chooses a master'
          : d.stage === 'arranging' ? mn ? 'Модоо эрэмбэлэх хугацаа' : 'Arrange your tiles' : prep ? mn ? 'Мод холих хугацаа' : 'Time to mix tiles' : masterBatchReveal
            ? mn ? 'Ноён моднуудаа буцаалаа' : 'Master returned the tiles'
            : revealingTribute && !showingSiblingRound ? mn ? 'Солилцооны моднууд' : 'Exchange tiles' : undefined} />
    </div>
    <div className="player-area daaluu-player-area">
      <div className="hand-header-bar" data-seat={game.mySeat}>
        {/* Same self-counter pill as Big Two / Muushig / 21, plus Daaluu's auto-play and size controls. */}
        <div className={`self-counter${own.tier === 'paid' ? ' is-vip' : ''}`} data-seat={game.mySeat} title={myTurn ? (mn ? 'Таны ээлж' : 'Your turn') : own.name}>
          <button type="button" className="icon-button pause-action-button"
            disabled={game.pause?.status === 'paused' || (game.pause?.status === 'requesting' && !!game.pause.pauseAgreedPlayerIds.includes(game.myPlayerId))}
            onClick={() => actions.pauseAction(game.pause?.status === 'requesting' ? 'agree_pause' : 'request')}
            aria-label={mn ? 'Тоглоом түр зогсоох' : 'Pause game'} title={mn ? 'Тоглоом түр зогсоох' : 'Pause game'}
            data-tooltip={mn ? 'Тоглоом түр зогсоох' : 'Pause game'}>
            <Icon name="pause" size={14} />
          </button>
          <span className="self-player-avatar" aria-hidden="true"
            style={{ background: myAvatar.gradient, borderColor: myAvatar.borderColor, boxShadow: `0 0 8px ${myAvatar.glow}` }}>
            {myAvatar.initial}
          </span>
          <span className="self-player-name">{own.name}</span>
          <span className="self-card-ratio" title={`${d.ledger.tea[game.mySeat]} ${mn ? 'цай' : 'tea'}`} aria-label={`${d.ledger.tea[game.mySeat]} ${mn ? 'цай' : 'tea'}`}>
            🍵 <strong>{d.ledger.tea[game.mySeat]}</strong>
          </span>
          <DaaluuSelfStatus game={game} mn={mn} />
          <ReactionPicker onSelect={actions.sendReaction} onOpenProfile={onOpenProfile} direction="up" compact />
          <DaaluuAutoPlayButton autoPlay={!!own.autoPlay} isVip={own.tier === 'paid'} mn={mn}
            onToggle={actions.setAutoPlay} onOpenProfile={onOpenProfile} />
          <DaaluuSizeControl mn={mn} />
        </div>
        <VoiceChatBar compact />
      </div>
      {reaction && <div className="daaluu-own-reaction"><ReactionBubble reactionId={reaction.reactionId} seq={reaction.seq} playerName={own.name} /></div>}
      <div className={`daaluu-hand-slot${returnDragGhost ? ' is-return-drop-target' : ''}`}
        data-daaluu-tribute-hand-target={masterBatchReturn && game.mySeat === tributeBatch?.masterSeat ? '' : undefined}
        data-drop-label={mn ? 'Модоо гарт буцаах' : 'Drop tile back in your hand'}>
        <DaaluuHand visible={!['mixing', 'retry', 'calling'].includes(d.stage)} collecting={collecting}
        identity={`${d.shuffle.roundId}:${game.myPlayerId}`} hand={masterBatchReturn ? d.hand.filter(tile => !assignedSet.has(tile.id)) : d.hand} mn={mn}
        label={d.stage === 'pair-call' ? mn ? 'Өнгө сонгохын өмнөх таны мод' : 'Your hand before choosing a color' : mn ? 'Таны мод' : 'Your tiles'}
        selected={selected} deadTileIds={d.deadTileIds} arriving={motion.arriving} selectionDisabled={selectionDisabled}
        onLayout={motion.rememberHandPositions}
        onPlayDragChange={setDraggingPlayId}
        onTributeDrop={masterBatchReturn && !disabled ? assignReturnToSlave : undefined}
        onPlayDrop={d.stage === 'playing' && !disabled ? id => {
          const tileIds = selected.includes(id) ? selected : [id];
          if (legalPlays.some(play => play.length === tileIds.length && play.every(tile => tileIds.includes(tile.id))))
            actions.daaluuAction({ type: 'play', tileIds });
        } : undefined}
        onSelect={d.stage === 'pair-call' ? undefined : id => {
          if (selectionDisabled) return;
          playCardSelectSound(!selected.includes(id));
          setSelected(current => current.includes(id) ? current.filter(tileId => tileId !== id) : current.length < selectionLimit ? [...current, id] : [id]);
        }} />
      </div>
      <div className="daaluu-action-slot">{(d.stage === 'playing' || contributing || exchanging || aravgui) &&
        /* Same controls row and play button as Big Two / Muushig; Daaluu has no pass. */
        <div className="player-controls">
          <div className="player-controls-primary">
            <button type="button" className="play-action" disabled={disabled || !selectionLegal} onClick={submitExchange}>
              <Icon name={exchanging && !giving ? 'arrow-left-right' : 'arrow-up'} />
              <span>{aravgui ? d.stage === 'aravgui-call' ? (mn ? '2 гэр эхлүүлэх' : 'Start 2 houses') : (mn ? '2 мод өгөх' : 'Give 2 tiles') : exchanging ? giving ? batchGiveRelay ? (mn ? 'Эхний боолд өгөх · Бэлэн' : 'Give to first slave · Ready') : (mn ? 'Ноёнд өгөх · Бэлэн' : 'Give to master · Ready') : (mn ? 'Солилцох' : 'Trade') : contributing ? (mn ? 'Өгөх' : 'Give tiles') : mn ? 'Гаргах' : 'Play tiles'} ({submitCount}{exchanging ? `/${exchangeCount}` : aravgui ? '/2' : ''})</span>
            </button>
          </div>
        </div>
      }</div>
    </div>
    <DaaluuMotion flights={motion.flights} mn={mn} />
    {returnFlights.length > 0 && createPortal(<div className="daaluu-motion-layer" aria-hidden="true">
      {returnFlights.map(flight => <span key={flight.key} className="daaluu-tile-flight" style={{
        '--from-x': `${flight.from.left + flight.from.width / 2}px`,
        '--from-y': `${flight.from.top + flight.from.height / 2}px`,
        '--to-x': `${flight.to.left + flight.to.width / 2}px`,
        '--to-y': `${flight.to.top + flight.to.height / 2}px`,
        animationDuration: `${DAALUU_FLIGHT_MS}ms`,
      } as CSSProperties} onAnimationEnd={() => setReturnFlights(current => current.filter(item => item.key !== flight.key))}>
        <DaaluuTile tile={flight.tile} />
      </span>)}
    </div>, document.body)}
    {returnDragGhost && d.hand.find(tile => tile.id === returnDragGhost.tileId) && createPortal(
      <div className="daaluu-hand-ghost" aria-hidden="true" style={{ left: returnDragGhost.x, top: returnDragGhost.y }}>
        <DaaluuTile tile={d.hand.find(tile => tile.id === returnDragGhost.tileId)!} />
      </div>, document.body)}
    {janliiPick && createPortal(<DaaluuJanliiReveal key={janliiPick.key} kindId={janliiPick.kindId} player={janliiPick.player} mn={mn} choices={janliiPick.choices} />, document.body)}
    {celebration && createPortal(<div key={celebration.key} className="daaluu-special-play" role="status">
      <ConfettiShower />
      <div className="special-combo-anim-container">
        {celebration.janliiKindId
          ? <div className="daaluu-janlii-title">
              <DaaluuTile tile={{ id: `janlii-announcement-${celebration.janliiKindId}`, kindId: celebration.janliiKindId }} />
              <div className="special-combo-anim-text">{celebration.label}</div>
            </div>
          : <div className="special-combo-anim-text">{celebration.label}</div>}
        <div className="special-combo-anim-sub">{celebration.player}</div>
      </div>
    </div>, document.body)}
      </div>
    </TableInteractionsProvider>
  );
}
